/**
 * The five answers to *who may add photos*, and the words for each — TROOP-225, §6.
 *
 * **One module, three readers.** The settings screen offers the choice, the gallery panel names who
 * does add when the reader does not, and the *Manage* row states the unit's answer at rest. Copy
 * lives on the client (`NotificationSettingsScreen`'s rule) and the server ships the enum, so this
 * is the one place any of these sentences exist — three copies is three that drift the day a sixth
 * option arrives.
 *
 * **The order here is presentation, not a ranking.** `AllAdults` and `AllOfficers` are
 * *incomparable* — an adult plain member is in one and a youth officer is in the other, and neither
 * set contains the other — so this is a list of five choices and never a slider with five stops. A
 * control that renders it as a range has to put those two in an order and whichever it picks is
 * wrong for somebody. `OPTIONS` runs widest-first because that is how somebody narrowing reads it,
 * and nothing may treat the index as a level.
 *
 * **No unit vocabulary in any of these strings** (rule 16). None of the five needs the unit's own
 * word for its young members: "officers" and "adults" are the axes themselves.
 */

/** Exactly the server's `PhotoUploadAudience`. A value off this list is a build talking to a newer API. */
export type PhotoUploadAudience =
  | 'Everyone'
  | 'AdultsAndOfficers'
  | 'AllAdults'
  | 'AllOfficers'
  | 'AdultOfficers';

interface AudienceCopy {
  /** The choice, as a short phrase. */
  label: string;
  /** Who that actually is, in a sentence — the thing somebody is really choosing between. */
  detail: string;
  /** The *Manage* row's tail: "On · anyone can add". Lowercase, it completes a clause. */
  summary: string;
  /**
   * Who fills this gallery, in a sentence. Two uses, and both are ordinary: the gallery renders it
   * where the upload control would have been for somebody who may not add, and its empty state
   * renders it to somebody who may — which is why `Everyone` needs real words here rather than
   * being left as the unreachable case.
   */
  whoDoes: string;
}

export const AUDIENCE_COPY: Record<PhotoUploadAudience, AudienceCopy> = {
  Everyone: {
    label: 'Everyone',
    detail: 'Anyone in the unit, and any parent. This is how a unit works unless it says otherwise.',
    summary: 'anyone can add',
    whoDoes: 'Anyone who can see this event can add photos.',
  },
  AdultsAndOfficers: {
    label: 'Adults and officers',
    detail: 'Every adult in the unit and every parent, plus your officers whatever their age.',
    summary: 'adults and officers add',
    whoDoes: 'Your unit lets its adults and its officers add the photos here.',
  },
  AllAdults: {
    label: 'All adults',
    detail: 'Any adult, whether or not they hold a position — parents included. No young members.',
    summary: 'adults add',
    whoDoes: 'Your unit lets its adults add the photos here.',
  },
  AllOfficers: {
    label: 'All officers',
    detail:
      'Anyone holding a position, including your young officers. No other adults, and no parents.',
    summary: 'officers add',
    whoDoes: 'Your unit lets its officers add the photos here.',
  },
  AdultOfficers: {
    label: 'Adult officers',
    detail: 'Adults who hold a position in the unit, and nobody else.',
    summary: 'adult leaders add',
    whoDoes: 'Your unit lets its adult leaders add the photos here.',
  },
};

/**
 * The five, widest first. Rendered as five separate choices — see the note above about why this
 * order is not a ranking.
 */
export const AUDIENCE_OPTIONS: PhotoUploadAudience[] = [
  'Everyone',
  'AdultsAndOfficers',
  'AllAdults',
  'AllOfficers',
  'AdultOfficers',
];

/**
 * An audience a build does not recognise falls back to the widest wording rather than crashing a
 * screen. It is only ever *copy* — every refusal is the server's, and a client that mislabels one
 * still cannot add a photograph.
 */
export function audienceCopy(audience: string | undefined): AudienceCopy {
  return AUDIENCE_COPY[audience as PhotoUploadAudience] ?? AUDIENCE_COPY.Everyone;
}

/**
 * The *Manage* row's value: the answer, never the category. "Off" alone when photos are off — a
 * unit that has turned them off has said nothing about who would add them, so naming an audience
 * there would be stating a decision nobody made.
 */
export function photoSettingSummary(
  photosEnabled: boolean,
  audience: string | undefined,
): string {
  return photosEnabled ? `On · ${audienceCopy(audience).summary}` : 'Off';
}
